import SvgIcon from '@/components/svg-icon/svgIcon'
import { Tag } from '@/components/tag/tag'
import { Typography } from '@/components/typography/typography'
import { ULR_QUERY_PARAM, mapCartStoreToUrlQuery } from '@/features/url-handling'
import { cn } from '@/libs/tw'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { formatCurrency } from '@/utils/formatCurrency'
import { translate } from '@/utils/translator'

import { useCrossSellingHotels } from '../hooks/useCrossSellingHotels'
import { CrossSellingHotel } from '../types'

export const CrossSellingBanner = () => {
	const { data } = useCrossSellingHotels()

	if (!data) return null

	const getCrossSellQueryString = (hotel: CrossSellingHotel) => {
		const cartStore = useCartStore.getState()

		const urlState = mapCartStoreToUrlQuery(cartStore)
		urlState.hotelCode = hotel.location.hotelCode

		const url = hotel.location.ibeUrl
		const searchParams = new URLSearchParams()

		searchParams.set(ULR_QUERY_PARAM, JSON.stringify(urlState))

		return `${url}?${searchParams.toString()}`
	}

	return (
		<div
			className={cn(
				'w-full rounded-8 text-textColor md:bg-naturel md:p-24 md:pb-32 md:text-crossSelling-textColorDesktop'
			)}
		>
			<div className={cn('mb-32 flex flex-col gap-16 md:mb-24 md:gap-8')}>
				<Typography element="h3" style="h3">
					{translate('cross-selling-title')}
				</Typography>
				<Typography element="p" style="p">
					{translate(data.length ? 'cross-selling-description' : 'cross-selling-no-hits')}
				</Typography>
			</div>
			<div className={cn('grid w-full grid-cols-1 gap-40 md:grid-cols-4 md:gap-24 ')}>
				{data.map((hotel) => (
					<a
						className={cn(
							'group relative flex gap-16 overflow-hidden rounded-8 text-textColor md:flex-col md:gap-0 md:bg-primary'
						)}
						key={hotel.location.hotelCode}
						href={getCrossSellQueryString(hotel)}
						target="_blank"
					>
						<div className={cn('absolute right-0 top-0 hidden p-12 md:block')}>
							<Tag type="naturel">
								<Typography element="span" style="p-s-bold">
									{translate('travel-period-from-price')}{' '}
									{formatCurrency(hotel.price)}
								</Typography>
							</Tag>
						</div>
						<img
							className={cn(
								'h-[104px] w-[104px] rounded-8 md:h-[150px] md:w-full md:rounded-0'
							)}
							src={hotel.location.image.largeUrl}
							alt={hotel.location.image.alt}
							title={hotel.location.image.title}
						/>
						<div
							className={cn(
								'flex flex-col gap-12 px-12 md:h-[50px] md:flex-row md:items-center md:justify-between'
							)}
						>
							<div className={cn('md:hidden')}>
								<Tag type="naturel">
									<Typography element="span" style="p-s">
										{formatCurrency(hotel.price)}
									</Typography>
								</Tag>
							</div>

							<Typography element="p" style="p-s-bold">
								{hotel.location.title}
							</Typography>
							<div
								className={cn(
									'flex items-center gap-12 rounded-8 border border-textColor px-16 py-12 md:hidden'
								)}
							>
								<Typography element="p" style="p-s">
									{translate('cross-selling-link')}
								</Typography>
								<SvgIcon
									iconName="arrow-right"
									className={cn('h-16 w-16 text-current')}
								/>
							</div>
						</div>
						<div
							className={cn(
								'absolute inset-0 hidden h-full w-full items-center justify-center gap-12 bg-crossSelling-hoverBackground text-crossSelling-hoverText opacity-0 transition-opacity duration-200 group-hover:opacity-100 md:flex'
							)}
						>
							<Typography element="p" style="p-ms-bold">
								{translate('cross-selling-link')}
							</Typography>
							<SvgIcon
								iconName="arrow-right"
								className={cn('h-12 w-12 text-current')}
							/>
						</div>
					</a>
				))}
			</div>
		</div>
	)
}
